/**
 * Define-related widgets:
 * - PrettyDefineWidget: L0 pretty widget for ::define header
 * - ButtonGroupWidget: Run/Stop + Pin + Config buttons
 * - ErrorMessageWidget: inline error message below define header
 */

import { WidgetType } from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";
import {
    attachSyntaxEditInteractions,
    createClearExecutionButton,
    createHoverDetail,
    createInlineRenderToggleButton,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTemplateInputsButton,
    createTextSpan,
    createWatchIndicator,
    shieldGroupEvents,
} from "../shared/widgets";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── PrettyDefineWidget — L0 pretty widget for ::define header ── */

export class PrettyDefineWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly pluginName: string,
        readonly summary: string,
        readonly isTemplate: boolean = false,
        readonly isCloudWorkerPlugin: boolean = false,
        readonly isWatched: boolean = false,
    ) {
        super();
    }
    eq(other: PrettyDefineWidget) {
        return (
            other.defId === this.defId &&
            other.pluginName === this.pluginName &&
            other.summary === this.summary &&
            other.isTemplate === this.isTemplate &&
            other.isCloudWorkerPlugin === this.isCloudWorkerPlugin &&
            other.isWatched === this.isWatched
        );
    }
    toDOM(): HTMLElement {
        const icon = createSyntaxBadge("D", this.isTemplate ? { sub: "T", template: true } : undefined);
        icon.classList.add("cm-magic-define-pretty-icon");

        const primary = [createTextSpan("cm-magic-define-pretty-id", this.defId)];
        if (this.pluginName) {
            primary.push(
                createTextSpan("cm-magic-define-pretty-plugin", this.pluginName),
            );
        }
        if (this.isCloudWorkerPlugin) {
            primary.push(createCloudWorkerIcon());
        }
        if (this.isTemplate) {
            primary.push(createTextSpan("cm-magic-define-pretty-template", "template"));
        }
        if (this.isWatched) {
            primary.push(createWatchIndicator(this.defId));
        }

        const wrapper = createPrettySyntaxShell({
            wrapperClass: "cm-magic-define-pretty",
            badge: icon,
            primary,
            detail: this.summary
                ? createHoverDetail("cm-magic-define-pretty-detail", [
                    createTextSpan("cm-magic-hover-kind cm-magic-define-pretty-kind", this.isTemplate ? `${this.pluginName} template` : this.pluginName),
                    createTextSpan("cm-magic-hover-summary cm-magic-define-pretty-summary", this.summary),
                ])
                : createHoverDetail("cm-magic-define-pretty-detail", [
                    createTextSpan("cm-magic-hover-kind cm-magic-define-pretty-kind", this.isTemplate ? `${this.pluginName} template` : this.pluginName),
                ]),
        });
        attachSyntaxEditInteractions(wrapper);
        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

function createCloudWorkerIcon(): HTMLElement {
    const icon = document.createElement("span");
    icon.className = "cm-magic-define-pretty-cloud";
    icon.title = tr("pluginSource.cloudWorker");
    icon.setAttribute("aria-label", tr("pluginSource.cloudWorker"));

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("aria-hidden", "true");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
        "d",
        "M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z",
    );
    svg.appendChild(path);
    icon.appendChild(svg);

    return icon;
}

/* ── ButtonGroupWidget — Run/Stop + Pin + Config buttons ── */

export class ButtonGroupWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly pluginName: string,
        readonly status: ExecutionStatus,
        readonly insideFor: boolean = false,
        readonly canInlineRender: boolean = false,
        readonly inlineRendered: boolean = false,
        readonly isTemplate: boolean = false,
    ) {
        super();
    }

    eq(other: ButtonGroupWidget) {
        return (
            other.defId === this.defId &&
            other.pluginName === this.pluginName &&
            other.status === this.status &&
            other.insideFor === this.insideFor &&
            other.canInlineRender === this.canInlineRender &&
            other.inlineRendered === this.inlineRendered &&
            other.isTemplate === this.isTemplate
        );
    }

    toDOM(): HTMLElement {
        const group = document.createElement("span");
        group.className = "cm-magic-btn-group";

        // Prevent ALL mouse events on the group from reaching CodeMirror,
        // so clicks here never trigger fold/unfold or cursor placement.
        shieldGroupEvents(group);

        const isRunning = this.status === "running";

        // Run/Stop button
        const runBtn = document.createElement("span");
        runBtn.className = isRunning
            ? "cm-magic-btn cm-magic-btn-stop"
            : "cm-magic-btn cm-magic-btn-run";
        runBtn.textContent = isRunning ? "■" : "▶";
        runBtn.title = isRunning
            ? tr("syntax.widget.define.stop", { pluginName: this.pluginName, id: this.defId })
            : tr("syntax.widget.define.run", { pluginName: this.pluginName, id: this.defId });
        runBtn.setAttribute(
            "aria-label",
            runBtn.title,
        );
        const defIdForEvent = this.defId;
        runBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isRunning) {
                window.dispatchEvent(
                    new CustomEvent("magic-md:cancel-definition", {
                        detail: { definitionId: defIdForEvent },
                    }),
                );
            } else {
                window.dispatchEvent(
                    new CustomEvent("magic-md:run-definition", {
                        detail: { definitionId: defIdForEvent },
                    }),
                );
            }
        });

        // Config button — opens the definition popover
        const cfgBtn = document.createElement("span");
        cfgBtn.className = "cm-magic-btn cm-magic-btn-config";
        cfgBtn.textContent = "⚙";
        cfgBtn.title = tr("syntax.widget.define.configure", { pluginName: this.pluginName, id: this.defId });
        cfgBtn.setAttribute("aria-label", cfgBtn.title);
        cfgBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = cfgBtn.getBoundingClientRect();
            const detail: {
                definitionId: string;
                x: number;
                y: number;
                sourceElement?: HTMLElement;
                unitKind?: "define";
            } = {
                definitionId: this.defId,
                x: rect.left,
                y: rect.bottom,
            };
            Object.defineProperty(detail, "sourceElement", {
                value: cfgBtn,
                enumerable: false,
            });
            Object.defineProperty(detail, "unitKind", {
                value: "define",
                enumerable: false,
            });
            window.dispatchEvent(
                new CustomEvent("magic-md:select-definition", {
                    detail,
                }),
            );
        });

        // Pin button — add/remove block in the workspace sidebar
        const pinBtn = document.createElement("span");
        pinBtn.className = "cm-magic-btn cm-magic-btn-pin";
        pinBtn.textContent = "⊙";
        pinBtn.title = tr("syntax.widget.define.pin", { pluginName: this.pluginName, id: this.defId });
        pinBtn.setAttribute("aria-label", pinBtn.title);
        pinBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            window.dispatchEvent(
                new CustomEvent("magic-md:pin-definition", {
                    detail: { definitionId: defIdForEvent },
                }),
            );
        });

        const inlineRenderBtn = createInlineRenderToggleButton(
            defIdForEvent,
            this.inlineRendered,
            this.inlineRendered
                ? tr("syntax.widget.define.hideInlineRender", { pluginName: this.pluginName, id: this.defId })
                : tr("syntax.widget.define.showInlineRender", { pluginName: this.pluginName, id: this.defId }),
        );

        // Inside for-blocks: only show config button (no run/pin)
        if (!this.insideFor && !this.isTemplate) {
            group.appendChild(runBtn);
        }
        group.appendChild(cfgBtn);
        if (this.isTemplate) {
            group.appendChild(
                createTemplateInputsButton(
                    "define",
                    defIdForEvent,
                    tr("syntax.widget.define.editTemplateInputs", { id: this.defId }),
                ),
            );
        }
        if (!this.insideFor) {
            if (this.canInlineRender && !this.isTemplate) {
                group.appendChild(inlineRenderBtn);
            }
            group.appendChild(pinBtn);
        }
        if (this.status === "error") {
            group.appendChild(
                createClearExecutionButton(
                    defIdForEvent,
                    tr("syntax.widget.define.clearError", { id: this.defId }),
                    tr("syntax.widget.define.clearError", { id: this.defId }),
                ),
            );
        }
        return group;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

/* ── ErrorMessageWidget — inline error message ── */

export class ErrorMessageWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly message: string,
    ) {
        super();
    }

    eq(other: ErrorMessageWidget) {
        return other.defId === this.defId && other.message === this.message;
    }

    toDOM(): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.className = "cm-magic-error-msg";

        const icon = document.createElement("span");
        icon.className = "cm-magic-error-icon";
        icon.textContent = "✗";

        const text = document.createElement("span");
        text.className = "cm-magic-error-text";
        text.textContent = this.message;

        const defLabel = document.createElement("span");
        defLabel.className = "cm-magic-error-def";
        defLabel.textContent = this.defId;

        wrapper.appendChild(icon);
        wrapper.appendChild(text);
        wrapper.appendChild(defLabel);
        return wrapper;
    }

    ignoreEvent(): boolean {
        return true;
    }
}
